/**
 * Общая схема цветовой темы приложения.
 *
 * 'system' зарезервирован под настройку «использовать схему операционной системы»,
 * которая появится в разделе настроек — логика разрешения режима уже здесь.
 */

export type ThemeMode = 'light' | 'dark' | 'system';

/** navTheme у ProLayout: 'realDark' включает darkAlgorithm для всего внутри layout */
export type NavTheme = 'light' | 'realDark';

const STORAGE_KEY = 'theme_mode';
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_MEDIA_QUERY).matches;
}

export function getStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'light';
  } catch {
    return 'light';
  }
}

export function setStoredThemeMode(mode: ThemeMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // localStorage недоступен (приватный режим / квота) — режим просто не сохранится
  }
}

/** Итоговое «темно ли», с учётом схемы ОС для режима 'system' */
export function isDarkMode(mode: ThemeMode): boolean {
  return mode === 'dark' || (mode === 'system' && getSystemPrefersDark());
}

export function navThemeFor(mode: ThemeMode): NavTheme {
  return isDarkMode(mode) ? 'realDark' : 'light';
}

/**
 * Нативные элементы браузера (в первую очередь полосы прокрутки) не знают про тему antd
 * и ориентируются на color-scheme документа — держим его в соответствии с выбранным режимом.
 */
export function applyDocumentColorScheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(
    'color-scheme',
    isDarkMode(mode) ? 'dark' : 'light',
  );
}

/** Подписка на смену схемы ОС. Возвращает функцию отписки. */
export function subscribeSystemThemeChange(listener: (prefersDark: boolean) => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }
  const mediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
  const handler = (event: MediaQueryListEvent) => listener(event.matches);
  mediaQuery.addEventListener('change', handler);
  return () => mediaQuery.removeEventListener('change', handler);
}
